import { ChangeDetectionStrategy, Component, computed, effect, HostBinding, input, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CapitalizePipe } from '@shared/pipes';
import { CarouselVr } from '../carousels';
import { IMediaVr, IVr360, IVrVideo } from '@core/models';

/** Component that displays virtual reality content section */
@Component({
  selector: 'app-section-vr',
  imports: [TranslateModule, CapitalizePipe, CarouselVr],
  templateUrl: "./section-vr.component.html",
  styleUrl: "./section-vr.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionVr {
  /** CSS classes applied to the host element */
  @HostBinding('class')
  protected get hostClass(): string {
    return this.variant() === 'geomarker' ? 'geomarker-vr-host' : 'row bg py-3 py-lg-4';
  }
  /** List of virtual reality videos received from parent component */
  readonly vrVideos = input<IVrVideo[]>([]);
  /** List of 360 tours received from parent component */
  readonly vr360 = input<IVr360[]>([]);
  readonly variant = input<'default' | 'geomarker'>('default');

  private readonly activeMediaIndex = signal(0);

  /** Combined list of VR content that interleaves videos and 360 tours */
  protected readonly vrMedia = computed<IMediaVr[]>(() => {
    const vrVideos = this.vrVideos().map((video) => ({
      ...video,
      isVideo: true,
      trackKey: `video:${video.id}`,
    }));
    const vr360 = this.vr360().map((tour) => ({
      ...tour,
      isVideo: false,
      trackKey: `360:${tour.id}`,
    }));

    const interleaved: IMediaVr[] = [];
    const maxLength = Math.max(vrVideos.length, vr360.length);
    for (let i = 0; i < maxLength * 2; i++) {
      if (i % 2 === 0 && vr360[Math.floor(i / 2)]) {
        interleaved.push(vr360[Math.floor(i / 2)]);
      } else if (i % 2 === 1 && vrVideos[Math.floor(i / 2)]) {
        interleaved.push(vrVideos[Math.floor(i / 2)]);
      }
    }
    return [...interleaved];
  });

  constructor() {
    effect(() => {
      this.vrMedia();
      this.activeMediaIndex.set(0);
    });
  }

  protected onMediaIndexChange(index: number): void {
    this.activeMediaIndex.set(index);
  }

  protected openSelectedMedia(): void {
    const item = this.vrMedia()[this.activeMediaIndex()];
    if (!item) return;
    const target = item.isVideo && 'web_link_youtube_video' in item
      ? item.web_link_youtube_video
      : item.url_content;
    if (target) window.open(target, '_blank', 'noopener,noreferrer');
  }
}
